/* eslint-disable no-param-reassign */
import {createSlice} from "@reduxjs/toolkit";
import type {PayloadAction} from "@reduxjs/toolkit";
import {v4 as uuid} from "uuid";
import type {HistoryEntry, Message, Role} from "../types/types";
import {setChatStorage} from "./storage";
import {cleanContent, removeEmoji} from "./utils/apiUtils";

export enum MenuValue {
    CHAT = "Chat",
    CONTACT = "Contact",
    DEFAULT = "Default",
}

export interface ElliCSState {
    messages: Record<string, Message>;
    history: HistoryEntry[];
    isLoading: boolean;
    menuValue: MenuValue;
    showScrollDownArrow?: boolean;
    enableScrollToBottom?: boolean;
    sessionId: string | null;
}

export interface IStore {
    elliCustomerService: ElliCSState;
}

const initialState = {
    messages: {},
    history: [],
    isLoading: false,
    menuValue: MenuValue.DEFAULT,
    showScrollDownArrow: false,
    enableScrollToBottom: false,
    sessionId: null,
} satisfies ElliCSState as ElliCSState;

const elliCSSlice = createSlice({
    name: "elliCustomerService",
    initialState,
    reducers: {
        rehydrateMessages(state, action: PayloadAction<Record<string, Message>>) {
            state.messages = action.payload;
        },
        addMessage(state, action: PayloadAction<Omit<Message, "role">>) {
            const {id, content, isStreaming} = action.payload;
            const existingMessage = state.messages[id];

            if (existingMessage) {
                state.messages[id] = {
                    ...existingMessage,
                    content: existingMessage.content + content,
                    isStreaming,
                    requestId: existingMessage.requestId || action.payload.requestId,
                };
            } else {
                state.messages[id] = {
                    ...action.payload,
                    role: "model",
                };
            }
            setChatStorage(state.messages);
        },
        addUserMessage(state, action: PayloadAction<string>) {
            const id = uuid();
            state.messages[id] = {
                id,
                content: JSON.stringify({nodeType: "markdown", value: action.payload}),
                role: "user",
            };
            setChatStorage(state.messages);
        },
        addHistoryEntry(state, action: PayloadAction<{role: Role; content: any}>) {
            const cleanedContent =
                typeof action.payload.content === "string"
                    ? removeEmoji(action.payload.content)
                    : cleanContent(action.payload.content);

            const entry = {
                role: action.payload.role,
                content:
                    typeof action.payload.content === "string"
                        ? [
                              {
                                  nodeType: "markdown",
                                  value: cleanedContent,
                              },
                          ]
                        : cleanedContent,
            };
            state.history.push(entry);
        },
        setIsLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        updateMessageFeedback(
            state,
            action: PayloadAction<{id: string; feedback: boolean | undefined}>,
        ) {
            const message = state.messages[action.payload.id];
            if (message) {
                message.feedbackThumbsUp = action.payload.feedback;
            }
            setChatStorage(state.messages);
        },
        setSessionId(state, action: PayloadAction<string | null>) {
            state.sessionId = action.payload;
        },
        clearChatState(state) {
            state.messages = {};
            state.history = [];
            state.isLoading = false;
            state.sessionId = null;
        },
        setMenuValue(state, action) {
            state.menuValue = action.payload;
        },
        setShowScrollDownArrow(state, action: PayloadAction<boolean>) {
            state.showScrollDownArrow = action.payload;
        },
        setEnableScrollToBottom(state, action: PayloadAction<boolean>) {
            state.enableScrollToBottom = action.payload;
        },
    },
});

export const {setMenuValue} = elliCSSlice.actions;
export const {addMessage} = elliCSSlice.actions;
export const {addUserMessage} = elliCSSlice.actions;
export const {addHistoryEntry} = elliCSSlice.actions;
export const {setIsLoading} = elliCSSlice.actions;
export const {updateMessageFeedback} = elliCSSlice.actions;
export const {rehydrateMessages} = elliCSSlice.actions;
export const {setShowScrollDownArrow} = elliCSSlice.actions;
export const {setEnableScrollToBottom} = elliCSSlice.actions;
export const {setSessionId} = elliCSSlice.actions;
export const {clearChatState} = elliCSSlice.actions;

export const elliCustomerServiceReducer = elliCSSlice.reducer;

export default elliCSSlice.reducer;
